import { create } from "zustand";
import {
  blacksmithApi,
  type BlacksmithConfig,
  type PlayerGemStack,
} from "../api/blacksmith";
import { inventoryApi } from "../api/inventory";
import type { MaterialStack, PlayerItem } from "../types";
import { useGameStore } from "./useGameStore";

type BlacksmithTab = "gems" | "craft" | "legendary";

interface BlacksmithStore {
  tab: BlacksmithTab;
  config: BlacksmithConfig | null;
  gems: PlayerGemStack[];
  shards: MaterialStack[];
  legendaryStones: MaterialStack[];
  epicItems: PlayerItem[];
  loading: boolean;
  actionLoading: boolean;
  message: string | null;
  error: string | null;

  setTab: (tab: BlacksmithTab) => void;
  refresh: () => Promise<void>;
  synthesizeGem: (gemType: string, fromLevel: number, luckLevel: number) => Promise<void>;
  sellGem: (gemType: string, level: number, quantity: number) => Promise<void>;
  exchangeGem: (fromGemType: string, toGemType: string, level: number) => Promise<void>;
  autoCraftGems: () => Promise<void>;
  autoCraftGemsFull: (confirmWithoutLuck?: boolean) => Promise<boolean>;
  previewAutoCraftFull: () => Promise<import("../api/blacksmith").AutoCraftFullPreview | null>;
  craftArmor: (slot: string, itemLevel: number, baseItemId?: number) => Promise<void>;
  legendaryUpgrade: (itemId: number) => Promise<void>;
}

export const useBlacksmithStore = create<BlacksmithStore>((set, get) => ({
  tab: "gems",
  config: null,
  gems: [],
  shards: [],
  legendaryStones: [],
  epicItems: [],
  loading: false,
  actionLoading: false,
  message: null,
  error: null,

  setTab: (tab) => set({ tab, error: null, message: null }),

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const [overview, config] = await Promise.all([
        inventoryApi.getOverview(),
        blacksmithApi.getConfig().catch(() => null),
      ]);
      const epicItems = (overview.items ?? []).filter((i) => i.template.rarity === "EPIC");
      set({
        gems: overview.gems ?? [],
        shards: overview.shards ?? [],
        legendaryStones: overview.legendary_stones ?? [],
        epicItems,
        config,
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load blacksmith data" });
    } finally {
      set({ loading: false });
    }
  },

  synthesizeGem: async (gemType, fromLevel, luckLevel) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.synthesizeGem(gemType, fromLevel, luckLevel);
      set({ gems: res.gems, message: res.message });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Synthesis failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  sellGem: async (gemType, level, quantity) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.sellGem(gemType, level, quantity);
      set({ gems: res.gems, message: res.message });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Sell failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  exchangeGem: async (fromGemType, toGemType, level) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.exchangeGem(fromGemType, toGemType, level);
      set({ gems: res.gems, message: res.message });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Exchange failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  autoCraftGems: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.autoCraftGems();
      set({ gems: res.gems, message: res.message });
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Auto-craft failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  previewAutoCraftFull: async () => {
    try {
      return await blacksmithApi.autoCraftGemsFullPreview();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Preview failed" });
      return null;
    }
  },

  autoCraftGemsFull: async (confirmWithoutLuck = false) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.autoCraftGemsFull(confirmWithoutLuck);
      set({ gems: res.gems, message: res.message });
      await useGameStore.getState().refreshBalance();
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Full auto-craft failed" });
      return false;
    } finally {
      set({ actionLoading: false });
    }
  },

  craftArmor: async (slot, itemLevel, baseItemId) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.craftArmor(slot, itemLevel, baseItemId);
      set({ message: res.message });
      await get().refresh();
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Craft failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  legendaryUpgrade: async (itemId) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await blacksmithApi.legendaryUpgrade(itemId);
      set({ message: res.message });
      await get().refresh();
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Upgrade failed" });
    } finally {
      set({ actionLoading: false });
    }
  },
}));
